import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { buildApiUrl } from '../utils/api/config';
import { newsAPI } from '../utils/api/newsAPI';
import './ManageChitrakootBlocks.css';

function ManageChitrakootBlocks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [savedNews, setSavedNews] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    description: '',
    category: 'chitrakoot',
    blockId: '',
    blockName: '',
    villageId: '',
    villageName: '',
    tags: '',
    image: null,
    isPublished: true
  });

  // Fetch blocks on component mount
  const fetchBlocks = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl('/locations/blocks?district=चित्रकूट'));
      const data = await response.json();
      if (data.success) {
        setBlocks(data.data);
      }
    } catch (error) {
      console.error('Error fetching blocks:', error);
      showToast('❌ Failed to load blocks', 'error');
    }
  }, []);

  const fetchVillagesByBlock = useCallback(async (blockId) => {
    try {
      const response = await fetch(buildApiUrl(`/locations/villages/block/${blockId}`));
      const data = await response.json();
      if (data.success) {
        setVillages(data.data);
      }
    } catch (error) {
      console.error('Error fetching villages:', error);
      showToast('❌ Failed to load villages', 'error');
    }
  }, []);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const refreshSavedNews = useCallback(async () => {
    try {
      setListLoading(true);
      const response = await newsAPI.getAdminNews({ category: 'chitrakoot', limit: 100 });
      if (response.success) {
        setSavedNews(response.data || []);
      }
    } catch (error) {
      showToast('❌ Failed to load saved news', 'error');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSavedNews();
  }, [refreshSavedNews]);

  useEffect(() => {
    if (formData.blockId) {
      fetchVillagesByBlock(formData.blockId);
    } else {
      setVillages([]);
      setFormData(prev => ({
        ...prev,
        villageId: '',
        villageName: ''
      }));
    }
  }, [fetchVillagesByBlock, formData.blockId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBlockChange = (e) => {
    const blockId = e.target.value;
    const selectedBlock = blocks.find(b => b._id === blockId);
    setFormData(prev => ({
      ...prev,
      blockId,
      blockName: selectedBlock?.name || ''
    }));
  };

  const handleVillageChange = (e) => {
    const villageId = e.target.value;
    const selectedVillage = villages.find(v => v._id === villageId);
    setFormData(prev => ({
      ...prev,
      villageId,
      villageName: selectedVillage?.name || ''
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const getImagePreview = () => {
    if (formData.image instanceof File) {
      return URL.createObjectURL(formData.image);
    }
    return null;
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.shortDescription || !formData.description) {
      showToast('❌ Please fill all required fields', 'error');
      return;
    }

    if (!formData.blockId) {
      showToast('❌ Please select a block', 'error');
      return;
    }

    if (!formData.villageId) {
      showToast('❌ Please select a village', 'error');
      return;
    }

    if (!formData.image) {
      showToast('❌ Please select an image', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('shortDescription', formData.shortDescription);
      submitData.append('description', formData.description);
      submitData.append('category', formData.category);
      submitData.append('blockId', formData.blockId);
      submitData.append('villageId', formData.villageId);
      submitData.append('isPublished', formData.isPublished ? 'true' : 'false');

      // Add tags
      const tagsList = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      tagsList.forEach(tag => {
        submitData.append('tags', tag);
      });

      // Add image
      submitData.append('featuredImage', formData.image);

      const response = await fetch(buildApiUrl('/news/add'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add news');
      }

      showToast('✅ News added successfully!');

      // Reset form
      setFormData({
        title: '',
        shortDescription: '',
        description: '',
        category: 'chitrakoot',
        blockId: '',
        blockName: '',
        villageId: '',
        villageName: '',
        tags: '',
        image: null,
        isPublished: true
      });
      setVillages([]);
      await refreshSavedNews();
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('क्या आप यह खबर हटाना चाहते हैं?')) {
      return;
    }

    try {
      const response = await newsAPI.deleteNews(id);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete news');
      }
      showToast('✅ News deleted successfully!');
      await refreshSavedNews();
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    }
  };

  return (
    <>
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <AdminShell
        eyebrow="Chitrakoot Desk"
        title="Publish block news"
        description="Add image-led local coverage for Chitrakoot blocks and villages with stronger editorial structure and cleaner publishing steps."
        primaryAction={{ label: 'Refresh list', onClick: refreshSavedNews }}
        secondaryAction={{ label: 'Dashboard', onClick: handleCancel }}
        meta={[
          { label: 'Blocks', value: String(blocks.length) },
          { label: 'Villages', value: String(villages.length) },
          { label: 'Saved news', value: String(savedNews.length) }
        ]}
      >
        <div className="manage-chitrakoot-blocks-container">
          <div className="blocks-wrapper">
            <form onSubmit={handleSubmit} className="blocks-form">
          {/* Location Selection */}
          <div className="form-section">
            <h3>📌 स्थान चुनें</h3>

            <div className="form-group">
              <label>ब्लॉक * (Block)</label>
              <select
                name="blockId"
                value={formData.blockId}
                onChange={handleBlockChange}
                required
                className="form-select"
              >
                <option value="">-- ब्लॉक चुनें --</option>
                {blocks.map(block => (
                  <option key={block._id} value={block._id}>
                    {block.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>गांव * (Village)</label>
              <select
                name="villageId"
                value={formData.villageId}
                onChange={handleVillageChange}
                required
                className="form-select"
                disabled={!formData.blockId}
              >
                <option value="">-- गांव चुनें --</option>
                {villages.map(village => (
                  <option key={village._id} value={village._id}>
                    {village.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* News Content */}
          <div className="form-section">
            <h3>📝 खबर की जानकारी</h3>

            <div className="form-group">
              <label>शीर्षक (Title) *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="खबर का शीर्षक दर्ज करें..."
                maxLength={200}
                required
              />
              <small>{formData.title.length}/200</small>
            </div>

            <div className="form-group">
              <label>संक्षिप्त विवरण (Short Description) *</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                placeholder="खबर का संक्षिप्त विवरण (सारांश)..."
                maxLength={500}
                required
                rows={3}
              />
              <small>{formData.shortDescription.length}/500</small>
            </div>

            <div className="form-group">
              <label>विस्तृत विवरण (Full Description) *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="खबर का पूरा विवरण..."
                required
                rows={6}
              />
              <small>शब्द गणना: {formData.description.split(/\s+/).filter(w => w).length}</small>
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-section">
            <h3>📸 तस्वीर अपलोड करें</h3>

            <div className="form-group">
              <label>मुख्य तस्वीर (Featured Image) *</label>
              <div className="image-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required
                  id="image-input"
                  className="image-input"
                />
                <label htmlFor="image-input" className="upload-label">
                  {formData.image ? (
                    <span className="upload-success">✓ {formData.image.name}</span>
                  ) : (
                    <>
                      <span className="upload-icon">📤</span>
                      <span>तस्वीर चुनने के लिए क्लिक करें या ड्रैग करें</span>
                    </>
                  )}
                </label>
              </div>

              {getImagePreview() && (
                <div className="image-preview">
                  <img src={getImagePreview()} alt="Preview" />
                </div>
              )}
            </div>
          </div>

          {/* Tags and Category */}
          <div className="form-section">
            <h3>🏷️ टैग्स और अतिरिक्त जानकारी</h3>

            <div className="form-group">
              <label>टैग्स (कॉमा से अलग करें)</label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="उदा. बुनियादी ढांचा, शिक्षा, स्वास्थ्य"
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isPublished"
                  checked={formData.isPublished}
                  onChange={handleInputChange}
                />
                <span>तुरंत प्रकाशित करें (Publish immediately)</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="btn-submit"
            >
              {loading ? 'प्रक्रिया चल रही है...' : '✓ खबर जोड़ें'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="btn-cancel"
            >
              ✕ रद्द करें
            </button>
          </div>
            </form>

            <section className="saved-chitrakoot-news">
              <h3>Saved Chitrakoot News</h3>
              {listLoading ? (
                <div className="saved-news-empty">Loading...</div>
              ) : savedNews.length === 0 ? (
                <div className="saved-news-empty">Abhi tak koi Chitrakoot news add nahi hui.</div>
              ) : (
                <div className="saved-chitrakoot-news-list">
                  {savedNews.map((item) => (
                    <article key={item._id} className="saved-chitrakoot-news-item">
                      <div>
                        <h4>{item.title}</h4>
                        <p>{item.blockName || 'Block unavailable'} {item.villageName ? `| ${item.villageName}` : ''}</p>
                      </div>
                      <div className="saved-chitrakoot-news-actions">
                        <button type="button" className="btn-cancel" onClick={() => navigate(`/admin/news/edit/${item._id}`)}>
                          Edit
                        </button>
                        <button type="button" className="btn-submit danger" onClick={() => handleDelete(item._id)}>
                          Delete
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </AdminShell>
    </>
  );
}

export default ManageChitrakootBlocks;
